import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Briefcase,
  CircleUserRound,
  Settings,
  Power,
  Sun,
  Moon,
  CreditCard,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { billingApi, SubscriptionStatus } from '../services/billingApi';
import { CreditBalanceWidget } from './CreditBalanceWidget';

export const UserAvatarMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Fetch subscription
  useEffect(() => {
    if (!user) return;
    billingApi.getSubscription()
      .then(sub => setSubscription(sub))
      .catch(err => {
        if (err?.status !== 404) {
          console.error('Failed to load subscription:', err);
        }
        setSubscription(null);
      });
  }, [user]);

  if (!user) return null;

  const getPlanName = (sub: SubscriptionStatus | null): string => {
    if (!sub) return 'Free Plan';
    if (sub.planName) return `${sub.planName} Plan`;
    if (!sub.planId) return 'Free Plan';
    if (sub.planId.includes('basic')) return 'Basic Plan';
    if (sub.planId.includes('professional')) return 'Professional Plan';
    if (sub.planId.includes('power')) return 'Power User Plan';
    return sub.status === 'active' ? 'Paid Plan' : 'Free Plan';
  };

  return (
    <div ref={menuRef} className="relative">
      {/* Avatar button */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-medical-600 dark:text-medical-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-medical-100 dark:hover:bg-medical-700/50 transition-all duration-200 focus:outline-none"
        title="Account & settings"
        aria-label="Account & settings"
      >
        <CircleUserRound className="h-5 w-5" strokeWidth={1.5} />
        <span className="text-sm font-medium">{user.title ? `${user.title} ${user.lastName}` : user.lastName || user.firstName}</span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-medical-800 rounded-xl shadow-xl border border-medical-100 dark:border-medical-700 py-1 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
          {/* User info header */}
          <div className="px-4 py-3 border-b border-medical-100 dark:border-medical-700">
            <p className="text-sm font-semibold text-medical-900 dark:text-medical-100 truncate">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-xs text-medical-500 dark:text-medical-400 truncate mt-0.5">
              {user.email}
            </p>
            <div className="mt-2">
              <CreditBalanceWidget compact />
            </div>
            <p className="text-xs font-medium text-medical-500 dark:text-medical-400 mt-1.5">
              {getPlanName(subscription)}
            </p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <Link
              to="/account"
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-medical-700 dark:text-medical-200 hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colours"
            >
              <CreditCard className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              Account
            </Link>
            <Link
              to="/account?tab=credentials"
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-medical-700 dark:text-medical-200 hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colours"
            >
              <Briefcase className="h-4 w-4 text-amber-500 dark:text-amber-400" />
              Professional Profile
            </Link>
            <Link
              to="/settings"
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-medical-700 dark:text-medical-200 hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colours"
            >
              <Settings className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              Settings
            </Link>
          </div>

          {/* Dark mode toggle */}
          <div className="border-t border-medical-100 dark:border-medical-700 py-1">
            <button
              onClick={(e) => { e.stopPropagation(); toggleTheme(); }}
              className="flex items-center justify-between w-full px-4 py-2 text-sm text-medical-700 dark:text-medical-200 hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colours"
            >
              <span className="flex items-center gap-2.5">
                {theme === 'dark'
                  ? <Moon className="h-4 w-4 text-indigo-400" />
                  : <Sun className="h-4 w-4 text-amber-500" />
                }
                Dark mode
              </span>
              {/* Toggle switch */}
              <div
                className={`relative w-9 h-5 rounded-full transition-colours duration-200 ${
                  theme === 'dark' ? 'bg-primary-600' : 'bg-medical-200 dark:bg-medical-600'
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    theme === 'dark' ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </div>
            </button>
          </div>

          {/* Report Issue */}
          <div className="border-t border-medical-100 dark:border-medical-700 py-1">
            <Link
              to="/settings/feedback"
              className="flex items-center gap-2.5 px-4 py-2 text-sm text-medical-700 dark:text-medical-200 hover:bg-medical-50 dark:hover:bg-medical-700/50 transition-colours"
            >
              <AlertCircle className="h-4 w-4 text-primary-500 dark:text-primary-400" />
              Report Issue
            </Link>
          </div>

          {/* Sign out */}
          <div className="border-t border-medical-100 dark:border-medical-700 py-1">
            <button
              onClick={() => { setIsOpen(false); logout(); }}
              className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colours"
            >
              <Power className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
